import React, { useEffect, useMemo, useState } from "react";
import {
  formatToTwoDecimalPlaces,
  isEmpty,
} from "../../../helper/Functions";
import { useTranslation } from "react-i18next";
import UserService from "../../../service/UserService";
import { API_URL } from "../../../helper/Constants";
import PaginatedRoleBasedDropdowns from "../FilterFormComponents/PaginatedRoleBasedDropdown";
import CustomTable from "../../../components/ui/tables/CustomTable";
import ManageUserChangePassword from "./ManageUserChangePassword";
import { ReactComponent as IconSearch } from "../../../assets/icons/icon-search.svg";
import { ReactComponent as IconTransfer } from "../../../assets/icons/icon-action-transfer.svg";
import { ReactComponent as IconReport } from "../../../assets/icons/icon-action-report.svg";
import { ReactComponent as IconChangePassword } from "../../../assets/icons/icon-action-change-password.svg";
import { useAuth } from "../../../provider/AuthContext";
import { toast } from "react-toastify";
import { FaUserTimes } from "react-icons/fa";
import UserStatusChangeConfirmDialog from "./UserStatusChangeConfirmDialog";
import { useNavigate } from "react-router-dom";
import ManageUserBalanceModal from "./ManageUserChangeBalanceLimit";
import SectionLoader from "components/ui/loaders/SectionLoader";
import RoleBasedDropdowns from "../FilterFormComponents/RoleBasedDropdown";

const ManageUser = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userService = new UserService(API_URL);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [responseMsg, setReponseMsg] = useState("");
  const [allUsersBalance, setAllUsersBalance] = useState([]);
  const [sortCriteria, setSortCriteria] = useState("balance");
  const [baseRole, setBaseRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedInputValues, setSelectedInputValues] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUserData, setSelectedUserData] = useState({});
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [userIdForPasswordUpdate, setUserIdForPasswordUpdate] = useState("");
  const [openUserStatusChangeConfirmModal, setOpenUserStatusChangeConfirmModal] = useState(false);
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);
  const [openBalanceLimitModal,setOpenBalanceLimitModal] = useState(false);
  const [selectedUserId,setSelecedUserId] = useState("");

  useEffect(() => {
    if (hasSearchedOnce) fetchUserBalance();
  }, [pagination]);

  const handleSortChange = (nextSortCriteria) => {
    setSortCriteria(nextSortCriteria);

    if (!hasSearchedOnce) return;

    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      return;
    }

    fetchUserBalance(nextSortCriteria);
  };

  const fetchUserBalance = async (sortValue = sortCriteria) => {
    setLoading(true);
    try {
      const {
        Partner: partner = null,
        SuperAgent: superAgent = null,
        Agent: agent = null,
        User: userInput = null,
      } = selectedInputValues;

      const dto = {
        type: selectedRole?.role || null,
        partnerId: partner?.value || undefined,
        superAgentId: superAgent?.value || undefined,
        agentId: agent?.value || undefined,
        userId: userInput?.value || undefined,
        sortBy: sortValue === "balance" ? "balance" : "username",
        sortOrder: sortValue === "balance" ? "desc" : "asc",
        limit: pagination.pageSize,
        page: pagination.pageIndex + 1,
      };

      const data = await userService.getUserBalance(dto);
      console.log("Fetched user balance data:", data);
      setAllUsersBalance(data.result);
      setTotalCount(data.pagination.total);
      setHasSearchedOnce(true);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch user balance.";
      toast.error(error.message);
      setReponseMsg(message);
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ pageIndex: 0, pageSize: 10 });
    fetchUserBalance();
  };

  const handleReset = () => {
    setBaseRole(null);
    setSelectedRole({});
    setSelectedInputValues({});
    setAllUsersBalance([]);
    setTotalCount(0);
    setSortCriteria("balance");
    
    setHasSearchedOnce(false);
  };

  const tableData = allUsersBalance?.map((item) => ({ ...item }));
  const totalUsers = allUsersBalance.length;
  const totalBal = allUsersBalance.reduce((acc, row) => acc + row.balance, 0);
  const totalNetworkBal = allUsersBalance.reduce((acc, row) => acc + row.networkBalance, 0);

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "createdBy",
        header: "User",
        size: 150,
        Cell: ({ cell }) => (
          <div className="flex items-center gap-2">
            {cell.row.original.createdBy && (
              <span className="flex items-center gap-2">
                {cell.row.original.createdBy}
                <span className="text-gray-700 -mt-0.5 text-2xl leading-3 font-mono font-lighter">→</span>
              </span>
            )}
            {cell.row.original?.name}
          </div>
        ),
        Footer: () => <>Total ({totalUsers} players)</>,
      },
      { accessorKey: "role", header: "Role", size: 80 },
      {
        accessorKey: "balance",
        header: "Balance",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {formatToTwoDecimalPlaces(cell.row.original.balance || 0)} {user?.currency}
          </>
        ),
        Footer: () => (
          <>
            {formatToTwoDecimalPlaces(totalBal)} {user?.currency}
          </>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => {
          const status = cell.row.original.status || "unknown";
          return (
            <div className={`font-medium ${status === "active" ? "text-green-600" : "text-red-600"}`}>
              {status}
            </div>
          );
        },
      },
      {
        accessorKey: "networkBalance",
        header: "Network Balance",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {formatToTwoDecimalPlaces(cell.row.original?.networkBalance || 0)} {user?.currency}
          </>
        ),
        Footer: () => (
          <>
            {formatToTwoDecimalPlaces(totalNetworkBal)} {user?.currency}
          </>
        ),
      },
      {
        accessorKey: "#",
        header: "Actions",
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex space-x-1">
            <span
              className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] text-sm text-[#242424] rounded-lg bg-white border border-black text-nowrap px-2 py-1"
              onClick={() => navigate("/admin/transfer", { state: { username: cell.row.original?.name } })}
            >
              <IconTransfer /> <span>{t("Transfer")}</span>
            </span>
            <span
              className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 text-sm text-nowrap px-2 py-1"
              title="Change Status"
              onClick={() => {
                if (user.username !== cell.row.original.createdBy) {
                  toast.error("Only creator can change status");
                  return;
                }
                setOpenUserStatusChangeConfirmModal(true);
                setSelectedUserData(cell.row.original);
              }}
            >
              <FaUserTimes /> <span>{t("Change Status")}</span>
            </span>
            <span
              onClick={() =>
                navigate("/admin/transfers-report", {
                  state: {
                    username: cell.row.original?.name,
                    userRole: cell.row.original?.role,
                    selectedUserId: cell.row.original?.id,
                  },
                })
              }
              className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 text-sm text-nowrap px-2 py-1"
            >
              <IconReport /> <span>{t("Report")}</span>
            </span>
            <span
              className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 text-sm text-nowrap px-2 py-1"
              onClick={() => {
                if (user.username !== cell.row.original.createdBy) {
                  toast.error("Only creator can change password");
                  return;
                }
                setOpenChangePasswordModal(true);
                setUserIdForPasswordUpdate(cell.row.original.id);
              }}
            >
              <IconChangePassword /> <span>Change Password</span>
            </span>
            {user.role === "Owner" && cell.row.original.role === "User" && <span
              className="cursor-pointer flex justify-center items-center gap-1 hover:bg-[#f2c41a] rounded-lg bg-white border border-black mx-1 text-sm text-nowrap px-2 py-1"
              onClick={() => {
                setOpenBalanceLimitModal(true);
                setSelecedUserId(cell.row.original.id);
              }}
            >
              <IconChangePassword /> <span>Change Balance limit</span>
            </span>}
          </div>
        ),
      },
    ],
    [totalUsers, totalBal, totalNetworkBal, user?.currency]
  );

  return (
    <div className="flex flex-col w-full">
      <header className="rounded-lg bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-medium md:text-left text-center">
          {t("Manage Users")}
        </h1>
      </header>

      <div className="flex-1 pt-5">
        <div className="w-full lg:flex rounded-lg">
          <PaginatedRoleBasedDropdowns
            showSelectRole
            baseRole={baseRole}
            setBaseRole={setBaseRole}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            selectedInputValues={selectedInputValues}
            setSelectedInputValues={setSelectedInputValues}
          />
        </div>

        <div className="lg:flex gap-3 mt-3 w-full lg:max-w-sm">
          <button
            className={`bg-primary hover:bg-primary/80 text-black w-full lg:max-w-36 font-bold py-2 rounded-lg ${
              !isEmpty(selectedRole) ? "" : "bg-[#f2c41a] cursor-not-allowed opacity-50"
            }`}
            type="button"
            onClick={handleSearch}
            disabled={isEmpty(selectedRole)}
          >
            {t("SEARCH")}
          </button>
          <button
            className="border border-black mt-4 sm:mt-0 lg:max-w-36 rounded-lg text-md hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 w-full"
            type="reset"
            onClick={handleReset}
          >
            {t("RESET")}
          </button>
        </div>

        <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

        {!loading ? (
          allUsersBalance?.length ? (
            <>
              {baseRole && (
                <div className="mb-4 flex flex-wrap items-center gap-y-2">
                  <span className="flex text-sm font-medium text-black">Sort players by:</span>
                  <div className="flex flex-wrap ml-2">
                  <label htmlFor="sortByUser" className="space-x-2 pr-4 ml-2">
                    <input
                      type="radio"
                      name="sortCriteria"
                      id="sortByUser"
                      value="alphabetically"
                      checked={sortCriteria === "alphabetically"}
                      onChange={(e) => handleSortChange(e.target.value)}
                    />
                    <span className="text-black font-medium">Name (A-Z)</span>
                  </label>
                  <label htmlFor="sortByBalance" className="space-x-2">
                    <input
                      type="radio"
                      name="sortCriteria"
                      id="sortByBalance"
                      value="balance"
                      checked={sortCriteria === "balance"}
                      onChange={(e) => handleSortChange(e.target.value)}
                    />
                    <span className="text-black font-medium">Balance (High to Low)</span>
                  </label>
                  </div>
                </div>
              )}

              <CustomTable
                handelSearchData={fetchUserBalance}
                columns={tableColumns}
                setPagination={setPagination}
                totalCount={totalCount}
                data={tableData || []}
                enableGlobalFilter={true}
                enableFilterMatchHighlighting={true}
                pagination={pagination}
              />

              {openChangePasswordModal && (
                <ManageUserChangePassword
                  isOpen={openChangePasswordModal}
                  onClose={() => setOpenChangePasswordModal(false)}
                  userIdForPasswordUpdate={userIdForPasswordUpdate}
                  setUserIdForPasswordUpdate={setUserIdForPasswordUpdate}
                />
              )}

              {openBalanceLimitModal && (
                <ManageUserBalanceModal
                  isOpen={openBalanceLimitModal}
                  onClose={() => setOpenBalanceLimitModal(false)}
                  userId={selectedUserId}
                  setUserId={setSelecedUserId}
                />
              )}

              {openUserStatusChangeConfirmModal && (
                <UserStatusChangeConfirmDialog
                  isOpen={openUserStatusChangeConfirmModal}
                  onClose={() => setOpenUserStatusChangeConfirmModal(false)}
                  selectedUserData={selectedUserData}
                  setSelectedUserData={setSelectedUserData}
                  onSuccess={() => {
                    toast.success(`Status changed successfully`);
                    fetchUserBalance();
                  }}
                />
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
              <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                <IconSearch />
              </div>
              <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                {responseMsg
                  ? responseMsg
                  : "Search all available records by selecting one of the filters above!"}
              </span>
            </div>
          )
        ) : (
          <div className="flex justify-center py-8">
            <SectionLoader />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUser;
